class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    const inputWhitelist = [".facet", ".collection-actions-wrapper"];
    const inputBlacklist = [".collection-actions-compare-toggle"];

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.debouncedOnSubmitReduced = debounce(
      (event) => {
        this.onSubmitHandler(event);
      },
      120,
    );

    this.addEventListener("input", (event) => {
      if (
        !inputWhitelist.some((selector) => !!event.target.closest(selector)) ||
        inputBlacklist.some((selector) => !!event.target.closest(selector))
      ) {
        return false;
      }

      // Invalidate the previous response as soon as the customer interacts.
      // Waiting for the debounce window allowed an older response to restore
      // the former checkbox before the new form state was submitted.
      FacetFiltersForm.cancelPendingRender();

      if (event.target.closest("price-range")) {
        this.debouncedOnSubmit(event);
      } else {
        this.debouncedOnSubmitReduced(event);
      }
    });

  }

  static getFitmentFacetKey(element) {
    const facetName = element?.closest(".js-filter")?.dataset.name || "";
    return FacetFiltersForm.singleSelectFitmentKeys.find(
      (key) =>
        facetName === `filter.p.m.custom.${key}` ||
        facetName.endsWith(`.${key}`),
    );
  }

  static enforceSingleFitmentSelection(element) {
    if (
      !element?.matches('input[type="checkbox"]') ||
      !element.checked ||
      !FacetFiltersForm.getFitmentFacetKey(element)
    ) {
      return;
    }

    element
      .closest(".js-filter")
      ?.querySelectorAll('input[type="checkbox"]')
      .forEach((input) => {
        if (input !== element) input.checked = false;
      });
  }

  static cancelPendingRender() {
    FacetFiltersForm.requestId += 1;
    FacetFiltersForm.abortController?.abort();
    FacetFiltersForm.abortController = null;
  }

  static getFitmentFacet(key) {
    return Array.from(document.querySelectorAll("#FacetFiltersForm .js-filter")).find(
      (facet) => {
        const name = facet.dataset.name || "";
        return name === `filter.p.m.custom.${key}` || name.endsWith(`.${key}`);
      },
    );
  }

  static splitFitmentValue(value) {
    return String(value || "")
      .split(";")
      .map((part) => part.trim())
      .filter(Boolean);
  }

  static getSyntheticSelection(key, paramName, records) {
    const urlParams = new URLSearchParams(window.location.search);
    const explicitSelection = urlParams.get(`tw_${key}`);
    if (explicitSelection && records.has(explicitSelection)) {
      return explicitSelection;
    }

    const selectedRawValues = new Set(urlParams.getAll(paramName));
    if (selectedRawValues.size === 0) return null;

    for (const [token, record] of records) {
      const rawValues = Array.from(record.rawValues);
      if (
        rawValues.length === selectedRawValues.size &&
        rawValues.every((rawValue) => selectedRawValues.has(rawValue))
      ) {
        return token;
      }
    }

    for (const rawValue of selectedRawValues) {
      if (records.has(rawValue)) return rawValue;
    }

    return null;
  }

  static normalizeDelimitedFitmentFacets() {
    for (const key of FacetFiltersForm.singleSelectFitmentKeys) {
      const facet = FacetFiltersForm.getFitmentFacet(key);
      const list = facet?.querySelector(".facet-options-list");
      const sourceInputs = Array.from(
        list?.querySelectorAll('input[type="checkbox"]:not([data-fitment-synthetic])') || [],
      );
      if (!list || sourceInputs.length === 0) continue;

      const paramName = sourceInputs[0].name;
      const records = new Map();

      sourceInputs.forEach((input) => {
        const rawValue = input.value.trim();
        const count = Number(
          input.closest("label")?.querySelector(".checkbox-facet-label-count")
            ?.textContent || 0,
        );

        FacetFiltersForm.splitFitmentValue(rawValue).forEach((token) => {
          if (!records.has(token)) {
            records.set(token, {
              rawValues: new Set(),
              count: 0,
              allDisabled: true,
            });
          }

          const record = records.get(token);
          record.rawValues.add(rawValue);
          record.count += Number.isFinite(count) ? count : 0;
          record.allDisabled = record.allDisabled && input.disabled;
        });
      });

      if (records.size === 0) continue;

      const selectedToken = FacetFiltersForm.getSyntheticSelection(
        key,
        paramName,
        records,
      );
      const templateItem = list.querySelector(".facet-options-list-item");
      const tokens = Array.from(records.keys()).sort((a, b) => {
        if (key === "year") return Number(a) - Number(b);
        return a.localeCompare(b, undefined, { numeric: true });
      });

      const fragment = document.createDocumentFragment();
      tokens.forEach((token, index) => {
        const record = records.get(token);
        const item = templateItem.cloneNode(true);
        const input = item.querySelector('input[type="checkbox"]');
        const label = item.querySelector("label");
        const labelText = item.querySelector(".checkbox-facet-label-text");
        const labelCount = item.querySelector(".checkbox-facet-label-count");
        const id = `filter-${key}-${index}-${token.replace(/[^a-z0-9]+/gi, "-")}`;

        input.id = id;
        input.name = paramName;
        input.value = token;
        input.dataset.fitmentSynthetic = "true";
        input.dataset.fitmentKey = key;
        input.dataset.rawValues = JSON.stringify(Array.from(record.rawValues));
        input.checked = token === selectedToken;
        input.disabled = record.allDisabled && !input.checked;
        input.setAttribute("aria-label", token);
        label.setAttribute("for", id);
        label.removeAttribute("style");
        label.removeAttribute("data-tooltip");
        label.removeAttribute("data-color");
        label.removeAttribute("data-image");
        labelText.textContent = token;
        if (labelCount) labelCount.textContent = record.count;

        const wrapper = input.closest(".checkbox-facet");
        wrapper?.classList.toggle(
          "checkbox-facet-disabled",
          input.disabled && !input.checked,
        );
        fragment.append(item);
      });

      list.replaceChildren(fragment);
      list.dataset.fitmentNormalized = "true";
    }
  }

  static normalizeActiveFitmentFacets() {
    const container = document.querySelector(".collection-actions-filters");
    if (!container) return;

    const params = new URLSearchParams(window.location.search);
    const labels = { year: "Year", make: "Make", model: "Model" };

    for (const key of FacetFiltersForm.singleSelectFitmentKeys) {
      const token = params.get(`tw_${key}`);
      if (!token) continue;

      container.querySelectorAll("facet-remove").forEach((remove) => {
        const text = remove.querySelector("span")?.textContent?.trim() || "";
        if (text.startsWith(`${labels[key]}:`)) remove.remove();
      });

      const removeUrl = new URL(window.location.href);
      removeUrl.searchParams.delete(`filter.p.m.custom.${key}`);
      removeUrl.searchParams.delete(`tw_${key}`);
      removeUrl.searchParams.delete("page");

      const remove = document.createElement("facet-remove");
      const link = document.createElement("a");
      link.href = removeUrl.toString();
      link.className = "collection-filter-button button button-secondary";

      const text = document.createElement("span");
      text.textContent = `${labels[key]}: ${token}`;
      const dismiss = document.createElement("span");
      dismiss.className = "collection-filter-dismiss";
      dismiss.textContent = "×";
      link.append(text, dismiss);
      remove.append(link);

      const clearAll = Array.from(container.children).find(
        (child) => child.querySelector?.(".button-text-link"),
      );
      container.insertBefore(remove, clearAll || null);
    }
  }

  static getAllowedModelsForSelectedMakes() {
    const makeFacet = FacetFiltersForm.getFitmentFacet("make");
    if (!makeFacet) return null;

    const selectedMakes = Array.from(
      makeFacet.querySelectorAll('input[type="checkbox"]:checked'),
      (input) => input.value,
    );
    if (selectedMakes.length === 0) return null;

    const allowedModels = new Set();
    for (const make of selectedMakes) {
      const makeModels = FacetFiltersForm.allowedModelsByMake[make];

      // A new catalog value should never accidentally lock customers out.
      if (!makeModels) return null;
      makeModels.forEach((model) => allowedModels.add(model));
    }

    return allowedModels;
  }

  static restoreModelConstraints() {
    const modelFacet = FacetFiltersForm.getFitmentFacet("model");
    modelFacet?.querySelectorAll('input[data-fitment-disabled="true"]').forEach((input) => {
      const shopifyDisabled = input.dataset.shopifyDisabled === "true";
      input.disabled = shopifyDisabled;
      input.closest(".checkbox-facet")?.classList.toggle(
        "checkbox-facet-disabled",
        shopifyDisabled,
      );
      delete input.dataset.fitmentDisabled;
      delete input.dataset.shopifyDisabled;
    });
  }

  static applyMakeModelConstraints() {
    FacetFiltersForm.restoreModelConstraints();

    const allowedModels = FacetFiltersForm.getAllowedModelsForSelectedMakes();
    const modelFacet = FacetFiltersForm.getFitmentFacet("model");
    if (!allowedModels || !modelFacet) return;

    modelFacet.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      // Keep an old invalid selection available so it can always be deselected.
      if (input.checked || allowedModels.has(input.value)) return;

      input.dataset.shopifyDisabled = input.disabled ? "true" : "false";
      input.dataset.fitmentDisabled = "true";
      input.disabled = true;
      input.closest(".checkbox-facet")?.classList.add("checkbox-facet-disabled");
    });
  }

  static clearInvalidModelSelections() {
    const allowedModels = FacetFiltersForm.getAllowedModelsForSelectedMakes();
    const modelFacet = FacetFiltersForm.getFitmentFacet("model");
    if (!allowedModels || !modelFacet) return;

    modelFacet.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
      if (!allowedModels.has(input.value)) input.checked = false;
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) {
        return;
      }

      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static getPreviewParams() {
    const previewParams = new URLSearchParams();
    const currentUrl = new URL(window.location.href);

    ["preview_theme_id", "pb", "_fd"].forEach((parameter) => {
      if (currentUrl.searchParams.has(parameter)) {
        previewParams.set(parameter, currentUrl.searchParams.get(parameter));
      }
    });

    const currentTheme = window.terrawagenThemePreview || window.Shopify?.theme;
    if (
      !previewParams.has("preview_theme_id") &&
      currentTheme?.id &&
      currentTheme?.role !== "main"
    ) {
      previewParams.set("preview_theme_id", currentTheme.id);
      previewParams.set("_fd", "0");
      previewParams.set("pb", "0");
    }

    return previewParams;
  }

  static getFilterSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams);
    ["preview_theme_id", "pb", "_fd", "section_id"].forEach((parameter) => {
      params.delete(parameter);
    });
    return params.toString();
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true, appendResults) {
    FacetFiltersForm.requestId += 1;
    const requestId = FacetFiltersForm.requestId;

    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
    }
    FacetFiltersForm.abortController = new AbortController();

    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");

    if (countContainer) {
      countContainer.classList.add("loading");
    }

    sections.forEach((section) => {
      const requestParams = new URLSearchParams(searchParams);
      requestParams.set("section_id", section.section);
      FacetFiltersForm.getPreviewParams().forEach((value, parameter) => {
        requestParams.set(parameter, value);
      });
      const url = `${window.location.pathname}?${requestParams.toString()}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId)
        : FacetFiltersForm.renderSectionFromFetch(
            url,
            event,
            appendResults,
            requestId,
            FacetFiltersForm.abortController.signal,
          );
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }

    if (!searchParams.includes("page")) {
      publish(PUB_SUB_EVENTS.collectionFilters, {
        filters: deserializeSearchParams(searchParams),
      });
    }
  }

  static renderSectionFromFetch(url, event, appendResults, requestId, signal) {
    fetch(url, { signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.requestId) {
          return;
        }

        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderCollectionActions(html);
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html, appendResults);
        FacetFiltersForm.renderProductCount(html);
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }

        console.error("Unable to update collection filters.", error);
        if (requestId === FacetFiltersForm.requestId) {
          document
            .getElementById("ProductGridContainer")
            ?.querySelector(".collection")
            ?.classList.remove("loading");
          document.getElementById("ProductCount")?.classList.remove("loading");
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    if (requestId !== FacetFiltersForm.requestId) {
      return;
    }

    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderCollectionActions(html);
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html, appendResults) {
    const container = document.getElementById("ProductGridContainer");
    const parsedDom = new DOMParser().parseFromString(html, "text/html");

    if (appendResults) {
      parsedDom
        .getElementById("product-grid")
        .prepend(...[...document.getElementById("product-grid").childNodes]);
    }
    container.innerHTML = parsedDom.getElementById("ProductGridContainer").innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount")?.innerHTML;
    const container = document.getElementById("ProductCount");

    if (!container) {
      return;
    }

    container.innerHTML = count;
    container.classList.remove("loading");
  }

  static renderCollectionActions(html) {
    const container = document.getElementById("CollectionActions");
    const parsedDom = new DOMParser().parseFromString(html, "text/html");

    if (!container) {
      return;
    }

    container.classList = parsedDom.getElementById("CollectionActions").classList;
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const oldFacetElements = Array.from(
      document.querySelectorAll("#FacetFiltersForm .js-filter"),
    );
    const newFacetElements = Array.from(
      parsedHTML.querySelectorAll("#FacetFiltersForm .js-filter"),
    );
    const triggeringFacetName = event?.target
      ?.closest(".js-filter")
      ?.dataset.name;

    const expandedFacets = [];
    const expandedFacetValues = [];
    document
      .querySelectorAll('#FacetFiltersForm .js-filter collapsible-expandable[expanded="true"]')
      ?.forEach((node) => {
        const filterName = node.closest(".js-filter").dataset.name;
        expandedFacets.push(filterName);
        // node is CollapsibleExpandable object
        if (!node.listCollapsed) {
          expandedFacetValues.push(filterName);
        }
      });

    const facetsContainer = document.querySelector(
      "#FacetFiltersForm .page-layout-sidebar-inner-content",
    );
    const focusedElement = document.activeElement;
    let focusedFilterId = null;
    if (facetsContainer?.contains(focusedElement)) {
      focusedFilterId = focusedElement.id;
    }

    if (facetsContainer) {
      const previousHeight = facetsContainer?.getBoundingClientRect().height;
      facetsContainer.style.minHeight = `${previousHeight}px`;
    }

    const newFacetNames = new Set(
      newFacetElements.map((element) => element.dataset.name),
    );
    oldFacetElements.forEach((facet) => {
      if (!newFacetNames.has(facet.dataset.name)) {
        facet.remove();
      }
    });

    newFacetElements.forEach((element) => {
      const filterName = element.dataset.name;
      const currentElement = oldFacetElements.find(
        (facet) => facet.dataset.name === filterName,
      );

      // Keep the facet the customer just used connected to the page. Replacing
      // that control during its own input event can discard the next click on
      // slower connections. Its checked/disabled/count state is synchronized
      // from Shopify's response instead.
      if (
        currentElement &&
        filterName === triggeringFacetName
      ) {
        FacetFiltersForm.syncFacetValues(currentElement, element);
        return;
      }

      if (currentElement) {
        currentElement.innerHTML = element.innerHTML;
      } else {
        facetsContainer.append(element);
      }

      const renderedElement = currentElement || element;

      const collapsible = renderedElement.querySelector("collapsible-expandable");
      const isExpanded = expandedFacets.includes(filterName);
      const hasExpandedValues = expandedFacetValues.includes(filterName);

      if (collapsible) {
        isExpanded ? collapsible.expand() : collapsible.collapse();
        hasExpandedValues ? collapsible.expandList() : collapsible.collapseList();
      }
    });

    if (focusedFilterId) {
      document.getElementById(focusedFilterId)?.focus({ preventScroll: true });
    }

    if (facetsContainer) {
      requestAnimationFrame(() => {
        facetsContainer.style.minHeight = "";
      });
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
  }

  static syncFacetValues(targetFacet, sourceFacet) {
    const sourceInputs = Array.from(sourceFacet.querySelectorAll("input"));
    const targetInputs = Array.from(targetFacet.querySelectorAll("input"));

    sourceInputs.forEach((sourceInput) => {
      const targetInput = targetInputs.find(
        (input) =>
          input.name === sourceInput.name && input.value === sourceInput.value,
      );
      if (!targetInput) {
        return;
      }

      targetInput.checked = sourceInput.checked;
      targetInput.disabled = sourceInput.disabled;

      const sourceWrapper = sourceInput.closest(".checkbox-facet");
      const targetWrapper = targetInput.closest(".checkbox-facet");
      if (sourceWrapper && targetWrapper) {
        targetWrapper.className = sourceWrapper.className;
      }

      const sourceCount = sourceInput
        .closest("label")
        ?.querySelector(".checkbox-facet-label-count");
      const targetCount = targetInput
        .closest("label")
        ?.querySelector(".checkbox-facet-label-count");
      if (sourceCount && targetCount) {
        targetCount.textContent = sourceCount.textContent;
      }
    });

    const sourceFooter = sourceFacet.querySelector(".facet-content-footer");
    const targetFooter = targetFacet.querySelector(".facet-content-footer");
    if (sourceFooter && targetFooter) {
      targetFooter.replaceWith(sourceFooter.cloneNode(true));
    } else if (sourceFooter && !targetFooter) {
      targetFacet
        .querySelector(".facet-content")
        ?.append(sourceFooter.cloneNode(true));
    } else if (!sourceFooter && targetFooter) {
      targetFooter.remove();
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".collection-actions-filters"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) {
        return;
      }

      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static updateURLHash(searchParams, replace) {
    const urlParams = new URLSearchParams(searchParams);
    FacetFiltersForm.getPreviewParams().forEach((value, parameter) => {
      urlParams.set(parameter, value);
    });
    const urlSearch = urlParams.toString();
    const url = `${window.location.pathname}${urlSearch && "?".concat(urlSearch)}`;

    if (replace) {
      history.replaceState({ searchParams }, "", url);
    } else {
      history.pushState({ searchParams }, "", url);
    }
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = document.getElementById("FacetsFilterForm");
    const searchParams = new URLSearchParams(new FormData(form)).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url);
  }

  onAjaxPagination(event) {
    event.preventDefault();
    const next = event.currentTarget;
    const url = next.href.indexOf("?") == -1 ? "" : next.href.slice(next.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url, event, true);
    scrollToElement(document.getElementById("ProductGridContainer"), -150);

    const params = new URLSearchParams(url);
    publish(PUB_SUB_EVENTS.pageChange, {
      page: Number(params.get("page")),
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = FacetFiltersForm.getFilterSearchParams(
  window.location.search,
);
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
FacetFiltersForm.requestId = 0;
FacetFiltersForm.abortController = null;
FacetFiltersForm.singleSelectFitmentKeys = ["year", "make", "model"];
FacetFiltersForm.allowedModelsByMake = {
  "Citroën": ["Jumper", "Jumper / Relay"],
  Fiat: ["Ducato"],
  Ford: ["Transit", "Transit 150 / 250 / 350"],
  Freightliner: ["Sprinter"],
  "Mercedes-Benz": ["Metris", "Sprinter", "V-Class", "Vito"],
  Peugeot: ["Boxer"],
  Ram: ["ProMaster"],
  Universal: ["Universal"],
  Volkswagen: ["Crafter", "ID. Buzz", "Transporter", "Vanagon"],
};
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this)),
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "") minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector("a").addEventListener("click", (event) => {
      event.preventDefault();
      const form =
        this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define("facet-remove", FacetRemove);

class AjaxPaginate extends HTMLElement {
  constructor() {
    super();
    const form = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
    const buttons = this.querySelectorAll("a");

    buttons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        form.onAjaxPagination(event);
      });
    });
  }
}

customElements.define("ajax-paginate", AjaxPaginate);

function scrollToElement(element, offset) {
  const elementRect = element.getBoundingClientRect();
  const absoluteElementTop = elementRect.top + window.pageYOffset;
  window.scrollTo({
    top: absoluteElementTop + offset,
    behavior: "smooth",
  });
}

/**
 * Accepts a query string from faceted filtering and returns a
 * deserialized object.
 *
 * @param {string} queryString The query string.
 * @returns {{}}
 */
const deserializeSearchParams = (queryString) => {
  const params = new URLSearchParams(queryString);
  const result = {};

  for (const [key, value] of params.entries()) {
    if (result[key]) {
      result[key] = `${result[key]},${value}`;
    } else if (value) {
      result[key] = value;
    }
  }

  return result;
};
